import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form,
  Row, Spinner, Table
} from 'react-bootstrap';
import { useAuth } from '../../components/auth/AuthProvider';
import { useToast } from '../../components/toast/ToastContext';
import { getRoles, createRole, deleteRole } from '../../controllers/roles';
import {
  getPermissionsMatrix,
  createPermission,
  deletePermission,
  setRolePermission,
} from '../../controllers/permissions';

const ROLE_LEVELS = ['miembro', 'nacional', 'región', 'diócesis', 'comunidad', 'equipo'];

// ─── Create Role Form ────────────────────────────────────────────────────────

function CreateRoleForm({ token, onCreated }) {
  const showToast = useToast();
  const [label, setLabel] = useState('');
  const [level, setLevel] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!label.trim() || level === '') return;
    setSaving(true);
    try {
      await createRole(token, { label: label.trim(), level: parseInt(level) });
      showToast({ message: 'Rol creado correctamente.', bg: 'success', autohide: true, delay: 5000, title: 'Rol creado' });
      setLabel('');
      setLevel('');
      onCreated();
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="g-2 align-items-end">
        <Col xs={12} md={5}>
          <Form.Group>
            <Form.Label>Nombre del rol</Form.Label>
            <Form.Control
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ej. Coordinador Nacional"
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Nivel</Form.Label>
            <Form.Select value={level} onChange={e => setLevel(e.target.value)} required>
              <option value="">-- Seleccionar nivel --</option>
              {ROLE_LEVELS.map((l, i) => (
                <option key={i} value={i}>{i} – {l}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Crear rol'}
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

// ─── Create Permission Form ──────────────────────────────────────────────────

function CreatePermissionForm({ token, onCreated }) {
  const showToast = useToast();
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!label.trim() || !key.trim()) return;
    setSaving(true);
    try {
      await createPermission(token, { label: label.trim(), permission_key: key.trim() });
      showToast({ message: 'Permiso creado correctamente.', bg: 'success', autohide: true, delay: 5000, title: 'Permiso creado' });
      setLabel('');
      setKey('');
      onCreated();
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="g-2 align-items-end">
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Descripción del permiso</Form.Label>
            <Form.Control
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ej. Administrar servicios"
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Clave (permission_key)</Form.Label>
            <Form.Control
              value={key}
              onChange={e => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="Ej. servicios_admin"
              required
              pattern="^[a-z0-9_]+$"
              title="Solo letras minúsculas, números y guiones bajos"
            />
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Crear permiso'}
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

// ─── Permissions Matrix Table ────────────────────────────────────────────────

function PermissionsMatrix({ token, roles, permissions, matrix, onMatrixChange, onDeleteRole, onDeletePermission }) {
  const showToast = useToast();
  const [toggling, setToggling] = useState({}); // key: `${role_id}_${perm_id}`
  const [deletingRole, setDeletingRole] = useState(null);
  const [deletingPerm, setDeletingPerm] = useState(null);

  async function handleToggle(role_id, permission_id, currentValue) {
    const key = `${role_id}_${permission_id}`;
    setToggling(prev => ({ ...prev, [key]: true }));
    try {
      await setRolePermission(token, { role_id, permission_id, enabled: !currentValue });
      onMatrixChange(role_id, permission_id, !currentValue);
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error' });
    } finally {
      setToggling(prev => ({ ...prev, [key]: false }));
    }
  }

  async function handleDeleteRole(role_id) {
    setDeletingRole(role_id);
    try {
      await deleteRole(token, role_id);
      showToast({ message: 'Rol eliminado.', bg: 'success', autohide: true, delay: 5000, title: 'Eliminado' });
      onDeleteRole(role_id);
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error' });
    } finally {
      setDeletingRole(null);
    }
  }

  async function handleDeletePerm(permission_id) {
    setDeletingPerm(permission_id);
    try {
      await deletePermission(token, permission_id);
      showToast({ message: 'Permiso eliminado.', bg: 'success', autohide: true, delay: 5000, title: 'Eliminado' });
      onDeletePermission(permission_id);
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error' });
    } finally {
      setDeletingPerm(null);
    }
  }

  if (!roles.length && !permissions.length) {
    return <Alert variant="warning">No tienes acceso a esta sección.</Alert>;
  }

  if (!roles.length || !permissions.length) {
    return (
      <Alert variant="info">
        {!roles.length ? 'No hay roles definidos.' : 'No hay permisos definidos.'}
      </Alert>
    );
  }

  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
      <Table bordered size="sm" className="align-middle" style={{ minWidth: 500 }}>
        <thead className="table-light">
          <tr>
            <th style={{ minWidth: 180, position: 'sticky', top: 0, left: 0, zIndex: 3, background: 'var(--bs-table-bg, #f8f9fa)' }}>Rol</th>
            {permissions.map(p => (
              <th key={p.permission_id} className="text-center" style={{ minWidth: 130, position: 'sticky', top: 0, zIndex: 2, background: 'var(--bs-table-bg, #f8f9fa)' }}>
                <div className="d-flex flex-column align-items-center gap-1">
                  <span className="small fw-semibold">{p.label}</span>
                  <code className="small text-muted">{p.permission_key}</code>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{ padding: '1px 6px', fontSize: '0.7rem' }}
                    disabled={deletingPerm === p.permission_id}
                    onClick={() => handleDeletePerm(p.permission_id)}
                    title="Eliminar permiso"
                  >
                    {deletingPerm === p.permission_id ? <Spinner size="sm" /> : '✕'}
                  </Button>
                </div>
              </th>
            ))}
            <th className="text-center" style={{ minWidth: 80, position: 'sticky', top: 0, zIndex: 2, background: 'var(--bs-table-bg, #f8f9fa)' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {roles.map(role => (
            <tr key={role.role_id}>
              <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--bs-body-bg, #fff)', borderRight: '2px solid #dee2e6' }}>
                <span className="fw-semibold">{role.label}</span>{' '}
                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                  nivel {role.level} – {ROLE_LEVELS[role.level] || '?'}
                </Badge>
              </td>
              {permissions.map(p => {
                const key = `${role.role_id}_${p.permission_id}`;
                const checked = !!(matrix[role.role_id] && matrix[role.role_id][p.permission_id]);
                return (
                  <td key={p.permission_id} className="text-center">
                    {toggling[key] ? (
                      <Spinner size="sm" />
                    ) : (
                      <Form.Check
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggle(role.role_id, p.permission_id, checked)}
                        aria-label={`${role.label} - ${p.label}`}
                      />
                    )}
                  </td>
                );
              })}
              <td className="text-center">
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={deletingRole === role.role_id}
                  onClick={() => handleDeleteRole(role.role_id)}
                  title="Eliminar rol"
                >
                  {deletingRole === role.role_id ? <Spinner size="sm" /> : 'Eliminar'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function RolesAdmin() {
  const { token } = useAuth();
  const showToast = useToast();

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'FAMA - Roles y Permisos (Admin)';
  }, []);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPermissionsMatrix(token);
      setRoles(data.roles || []);
      setPermissions(data.permissions || []);
      setMatrix(data.matrix || {});
    } catch (err) {
      showToast({ message: err.message, bg: 'danger', autohide: true, delay: 7000, title: 'Error al cargar datos' });
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  function handleMatrixChange(role_id, permission_id, enabled) {
    setMatrix(prev => {
      const next = { ...prev };
      if (!next[role_id]) next[role_id] = {};
      if (enabled) {
        next[role_id] = { ...next[role_id], [permission_id]: true };
      } else {
        const rolePerms = { ...next[role_id] };
        delete rolePerms[permission_id];
        next[role_id] = rolePerms;
      }
      return next;
    });
  }

  function handleDeleteRole(role_id) {
    setRoles(prev => prev.filter(r => r.role_id !== role_id));
    setMatrix(prev => {
      const next = { ...prev };
      delete next[role_id];
      return next;
    });
  }

  function handleDeletePermission(permission_id) {
    setPermissions(prev => prev.filter(p => p.permission_id !== permission_id));
    setMatrix(prev => {
      const next = {};
      for (const [rid, perms] of Object.entries(prev)) {
        const filteredPerms = { ...perms };
        delete filteredPerms[permission_id];
        next[rid] = filteredPerms;
      }
      return next;
    });
  }

  return (
    <Container className="py-4">
      <h1 className="mb-4">Roles y Permisos</h1>

      {/* Create Role */}
      <Card className="mb-4">
        <Card.Header><strong>Crear nuevo rol</strong></Card.Header>
        <Card.Body>
          <CreateRoleForm token={token} onCreated={loadMatrix} />
        </Card.Body>
      </Card>

      {/* Create Permission */}
      <Card className="mb-4">
        <Card.Header><strong>Crear nuevo permiso</strong></Card.Header>
        <Card.Body>
          <CreatePermissionForm token={token} onCreated={loadMatrix} />
        </Card.Body>
      </Card>

      {/* Matrix */}
      <Card>
        <Card.Header><strong>Asignación de permisos por rol</strong></Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <PermissionsMatrix
              token={token}
              roles={roles}
              permissions={permissions}
              matrix={matrix}
              onMatrixChange={handleMatrixChange}
              onDeleteRole={handleDeleteRole}
              onDeletePermission={handleDeletePermission}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default RolesAdmin;
